import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { useTranslation } from 'react-i18next'
import QRCode from 'react-qr-code'

import { ChevronLeft, Info, Pix } from '@maistodos/core-icons'
import {
  Box,
  Button,
  Divider,
  Skeleton,
  toast,
  Typography,
  useTheme,
} from '@maistodos/design-system-web'

import {
  AlertContainer,
  AlertContent,
  AlertIconWrapper,
  AlertMessage,
  AlertTitle,
} from '@/components/Alert'
import { FeedbackModal } from '@/components/FeedbackModal'
import {
  LinkStatus,
  PaymentOption,
} from '@/modules/checkout/_core/domain/entities'
import { SectionGroup } from '@/modules/checkout/components/Section/SectionGroup'
import { SectionText } from '@/modules/checkout/components/Section/SectionText'
import { SectionTitle } from '@/modules/checkout/components/Section/SectionTitle'
import { useLink } from '@/modules/checkout/hooks/use-link'
import { useModal } from '@/modules/checkout/hooks/use-modal'
import { usePayGenerate } from '@/modules/checkout/hooks/use-pay-generate'
import { PaymentFlowAnalytics } from '@/shared/utils/analytics'
import { copyToClipboard } from '@/shared/utils/copy-to-clipboard'
import { formatLocaleCurrency } from '@/shared/utils/format-locale'
import { cn } from '@/shared/utils/tailwind'

import { FIVE_SECONDS, REFETCH_RUN_LIMIT } from './constants'
import { PixAutomaticIcon } from './PixAutomaticIcon'
import { PixOptionCard } from './PixOptionCard'
import { RecommendedBadge } from './RecommendedBadge'
import { FormPixProps, PixOption } from './types'

export const FormPix = ({ id, amount, pix, automaticPix }: FormPixProps) => {
  const { t, i18n } = useTranslation()
  const theme = useTheme()

  const refetchTimes = useRef<number>(0)
  const [refetchInterval, setRefetchInterval] = useState<number | false>(0)
  const [selected, setSelected] = useState<PixOption | null>(null)

  const hasBothOptions = Boolean(pix && automaticPix)

  const selectedPaymentType = useMemo(() => {
    if (selected === PaymentOption.AutomaticPixCopyPaste) return automaticPix
    if (selected === PaymentOption.Pix) return pix

    return undefined
  }, [selected, pix, automaticPix])

  useEffect(() => {
    if (hasBothOptions) return

    setSelected(
      pix
        ? PaymentOption.Pix
        : automaticPix
          ? PaymentOption.AutomaticPixCopyPaste
          : null,
    )
  }, [hasBothOptions, pix, automaticPix])

  const onChangeType = useCallback((value: PixOption) => {
    PaymentFlowAnalytics.onClickPixType(value)

    refetchTimes.current = 0
    setRefetchInterval(0)

    setSelected(value)
  }, [])

  const onClearType = useCallback(() => {
    refetchTimes.current = 0
    setRefetchInterval(false)

    setSelected(null)
  }, [])

  const {
    isOpen: isOpenErrorModal,
    open: openErrorModal,
    close: closeErrorModal,
  } = useModal()

  const { data, isLoading, error, isError } = usePayGenerate(
    selected ?? PaymentOption.Pix,
    id,
    selectedPaymentType?.id,
  )

  const code = useMemo(() => data?.pix?.qrCode || '', [data])

  const formatted = useMemo<string>(
    () => formatLocaleCurrency(amount, i18n.language),
    [amount, i18n.language],
  )

  const isAutomatic = selected === PaymentOption.AutomaticPixCopyPaste

  const errorTitle = useMemo(() => {
    if (error?.needs_to_pay_with_max_installments?.length) {
      return error.needs_to_pay_with_max_installments[0]
    }

    return t('Não foi possível processar seu pagamento.')
  }, [error, t])

  const onCopy = useCallback(async () => {
    PaymentFlowAnalytics.onClickCopyCode()

    await copyToClipboard(code)

    toast.success({
      message: t('Cópia realizada com sucesso!'),
      duration: 3000,
    })
  }, [code, t])

  useEffect(() => {
    if (isError) {
      openErrorModal()
    }
  }, [isError, openErrorModal])

  // Long-polling to automatically redirect to the receipt screen if Pix got confirmed
  const { isFetching, data: link } = useLink(id, {
    notifyOnChangeProps: ['isFetching', 'data'],
    refetchInterval,
  })

  useEffect(() => {
    if (code) {
      setRefetchInterval(FIVE_SECONDS)
    }
  }, [code])

  useEffect(() => {
    if (isFetching) {
      refetchTimes.current++
    }

    if (refetchTimes.current >= REFETCH_RUN_LIMIT) {
      setRefetchInterval(false)
    }
  }, [isFetching])

  const linkStatus = link?.status

  useEffect(() => {
    if (linkStatus === LinkStatus.Paid && selected) {
      PaymentFlowAnalytics.onApprovedPayment(selected, id)
    }
  }, [id, linkStatus, selected])

  return (
    <div className="flex flex-col gap-spacing16">
      {hasBothOptions && !selected && (
        <div className="flex flex-col gap-spacing16">
          <PixOptionCard
            icon={<PixAutomaticIcon color={theme.colors.brand500} />}
            recommended
            title={t('Pix Automático')}
            description={t(
              'autorize este e os próximos pagamentos de forma automática.',
            )}
            onClick={() => onChangeType(PaymentOption.AutomaticPixCopyPaste)}
          />

          <PixOptionCard
            icon={<Pix color={theme.colors.brand500} fontSize="24px" />}
            title={t('Pix Avulso')}
            description={t(
              'pagamento único. Prefira o Pix Automático para não precisar autorizar novamente todos os meses.',
            )}
            onClick={() => onChangeType(PaymentOption.Pix)}
          />
        </div>
      )}

      {hasBothOptions && selected && (
        <button
          type="button"
          onClick={onClearType}
          className="flex w-fit cursor-pointer items-center gap-spacing4 text-small font-bold text-brand500 hover:underline"
        >
          <span aria-hidden>
            <ChevronLeft color={theme.colors.brand500} fontSize="16px" />
          </span>
          {t('Voltar')}
        </button>
      )}

      {selected && (
        <div
          className={cn(
            'flex w-full flex-col gap-spacing24',
            automaticPix
              ? 'rounded-small border border-neutral200 px-spacing12 pb-spacing12 pt-spacing24'
              : 'md:w-1/2',
          )}
        >
          {automaticPix && (
            <>
              <div className="flex items-center gap-spacing8">
                <Typography variant="large" weight="bold" color="neutral900">
                  {isAutomatic ? t('Pix Automático') : t('Pix Avulso')}
                </Typography>

                {isAutomatic && <RecommendedBadge />}
              </div>

              <Divider color="neutral200" />
            </>
          )}

          {isLoading && (
            <div className="flex w-full flex-col gap-spacing24">
              <SectionGroup>
                <SectionTitle>{t('Valor do PIX')}</SectionTitle>
                <SectionText>{formatted}</SectionText>
              </SectionGroup>
              <SectionGroup>
                <SectionTitle>{t('Chave copia e cola:')}</SectionTitle>
                <Skeleton height={16} label="Carregando" />
                <Skeleton height={16} label="Carregando" />
                <Skeleton height={16} label="Carregando" />
                <Skeleton height={16} label="Carregando" />
              </SectionGroup>
              <div className={cn('mx-auto w-full', automaticPix && 'md:w-2/3')}>
                <Button variant="primary" fullWidth disabled loading>
                  {t('Copiar chave')}
                </Button>
              </div>
              <div className="flex flex-col items-center gap-spacing4">
                <Skeleton height={180} width={180} label="Carregando" />
                <div className="flex flex-col items-center gap-spacing2">
                  <Skeleton height={12} width={150} label="Carregando" />
                  <Skeleton height={12} width={50} label="Carregando" />
                </div>
              </div>
            </div>
          )}

          {code ? (
            <div className="flex w-full flex-col gap-spacing24">
              <SectionGroup>
                <SectionTitle>{t('Valor do PIX')}</SectionTitle>
                <SectionText>{formatted}</SectionText>
              </SectionGroup>

              <SectionGroup>
                <SectionTitle>{t('Chave copia e cola:')}</SectionTitle>
                {automaticPix ? (
                  <div className="rounded-xsmall bg-neutral100 p-spacing12">
                    <Typography
                      variant="caption"
                      color="neutral600"
                      className="break-words"
                    >
                      {code}
                    </Typography>
                  </div>
                ) : (
                  <SectionText className="break-words">{code}</SectionText>
                )}
              </SectionGroup>

              <div className={cn('mx-auto w-full', automaticPix && 'md:w-2/3')}>
                <Button
                  variant="primary"
                  onClick={onCopy}
                  testID="copy"
                  fullWidth
                >
                  {t('Copiar chave')}
                </Button>
              </div>

              <div className="mx-auto flex w-full max-w-[180px] flex-col">
                <Box className="mx-auto my-spacing0 h-auto w-full max-w-full">
                  <QRCode
                    size={256}
                    className={'h-auto w-full max-w-full'}
                    viewBox="0 0 256 256"
                    value={code}
                  />
                </Box>

                <Typography variant="caption" align="center">
                  {t('Mire a câmera do seu celular para o QRCODE')}
                </Typography>
              </div>
            </div>
          ) : null}
        </div>
      )}

      {isError && (
        <AlertContainer variant="warning">
          <AlertIconWrapper>
            <Info color="currentColor" fontSize="24px" />
          </AlertIconWrapper>
          <AlertContent>
            {errorTitle ? <AlertTitle>{errorTitle}</AlertTitle> : null}
            <AlertMessage>
              {t(
                'Verifique as informações do seu pagamento e tente novamente.',
              )}
            </AlertMessage>
          </AlertContent>
        </AlertContainer>
      )}

      <FeedbackModal
        type="error"
        isOpen={isOpenErrorModal}
        onClose={closeErrorModal}
        title={errorTitle}
        description={t(
          'Verifique as informações do seu pagamento e tente novamente.',
        )}
        footer={
          <Button variant="secondary" fullWidth onClick={closeErrorModal}>
            Fechar
          </Button>
        }
      />
    </div>
  )
}
